<template>
  <div class="">
    <form
      class="add-form"
      ref="customer_form"
      @keydown.enter.prevent="() => {}"
    >
      <div class="row">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header">
              <h4 class="mb-0">{{ $t("ELIXIOPAY.PAYMENT_INFORMATION") }}</h4>
            </div>
            <div class="card-body">
              <!-- Formulaire de paiement -->
              <div v-if="!paymentUrl">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <base-input
                        :label="`${$t('COMMON.ORGANIZATION')} (*)`"
                        :placeholder="$t('COMMON.ORGANIZATION')"
                        v-if="
                          $currentUserCan(
                            $permissions.PERM_VIEW_ANY_ORGANIZATIONS
                          ) && !hideOrganization
                        "
                      >
                        <organization-selector
                          :organization="transaction.customer?.organization?.id"
                          :filterable="true"
                          :showAll="false"
                          @organizationChanged="
                            (organizationId, organization) => {
                              selectedOrganizationId = organizationId;
                              organizationName = organization.name;
                              selectedCustomer = {};
                              onFormChanged();
                            }
                          "
                          :disabled="!!transaction.id"
                        />
                      </base-input>
                      <validation-error
                        :errors="apiValidationErrors.organization"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <base-input
                        :label="`${$t('COMMON.CUSTOMER')}`"
                        :placeholder="$t('COMMON.CUSTOMER')"
                        v-if="
                          $currentUserCan($permissions.PERM_VIEW_ANY_CUSTOMERS)
                        "
                      >
                        <customer-selector
                          :customer="transaction.customer?.id"
                          :filterable="true"
                          :showAll="false"
                          :filterOrganization="selectedOrganizationId"
                          @customerChanged="
                            (customerId, customer) => {
                              selectedCustomer = customer.id;
                              customerName = `${customer.firstname} ${customer.lastname}`;
                              onFormChanged();
                            }
                          "
                        />
                      </base-input>
                      <validation-error
                        :errors="apiValidationErrors.customer"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-12 form-group">
                  <base-input
                    :label="`${$t('ELIXIOPAY.INVOICE_CODE')} (*)`"
                    :placeholder="$t('ELIXIOPAY.INVOICE_CODE_PLACEHOLDER')"
                    v-model="paymentData.invoice_code"
                    required
                  />
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t("COMMON.AMOUNT") }} *</label>
                      <base-input
                        v-model="paymentData.amount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        :placeholder="$t('COMMON.AMOUNT')"
                        required
                      />
                      <validation-error
                        :errors="apiValidationErrors.is_default"
                      />
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t("COMMON.CURRENCY") }} *</label>
                      <el-select
                        v-model="paymentData.currency"
                        :placeholder="$t('COMMON.CURRENCY')"
                        class="w-100"
                      >
                        <el-option label="CAD - Dollar Canadien" value="CAD" />
                        <el-option label="USD - Dollar Américain" value="USD" />
                        <el-option label="EUR - Euro" value="EUR" />
                      </el-select>
                    </div>
                  </div>
                </div>

                <div class="col-md-12 form-group">
                  <label>{{ $t("ELIXIOPAY.PAYMENT_METHOD") }} *</label>
                  <el-select
                    v-model="paymentData.payment_method"
                    :placeholder="$t('ELIXIOPAY.PAYMENT_METHOD')"
                    class="w-100"
                  >
                    <el-option :label="$t('ELIXIOPAY.METHOD_CB')" value="CB" />
                    <el-option
                      :label="$t('ELIXIOPAY.METHOD_TFE')"
                      value="TFE"
                    />
                  </el-select>
                </div>

                <div class="col-md-12 form-group">
                  <label>{{ $t("COMMON.DESCRIPTION") }}</label>
                  <base-input>
                    <textarea
                      v-model="paymentData.description"
                      class="form-control"
                      rows="3"
                      :placeholder="$t('COMMON.DESCRIPTION')"
                    ></textarea>
                  </base-input>
                </div>

                <div class="text-right">
                  <base-button
                    type="primary"
                    @click="initiatePayment"
                    :loading="processing"
                    :disabled="!canInitiate"
                  >
                    <i class="fas fa-credit-card mr-2"></i>
                    {{ $t("ELIXIOPAY.INITIATE_PAYMENT") }}
                  </base-button>
                </div>
              </div>

              <!-- Iframe de paiement -->
              <div v-else>
                <div class="alert alert-info">
                  <i class="fas fa-info-circle mr-2"></i>
                  {{ $t("ELIXIOPAY.PAYMENT_IFRAME_INFO") }}
                </div>

                <div class="payment-iframe-container">
                  <iframe
                    :src="paymentUrl"
                    frameborder="0"
                    class="payment-iframe"
                    @load="onIframeLoad"
                  ></iframe>
                </div>

                <div class="mt-3 text-center">
                  <base-button
                    type="secondary"
                    @click="checkPaymentStatus"
                    :loading="checkingStatus"
                  >
                    <i class="fas fa-sync mr-2"></i>
                    {{ $t("ELIXIOPAY.CHECK_STATUS") }}
                  </base-button>

                  <base-button type="warning" @click="resetForm" class="ml-2">
                    <i class="fas fa-redo mr-2"></i>
                    {{ $t("ELIXIOPAY.NEW_PAYMENT") }}
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="card">
            <div class="card-header">
              <h4 class="mb-0">{{ $t("ELIXIOPAY.PAYMENT_SUMMARY") }}</h4>
            </div>
            <div class="card-body">
              <div class="summary-item" v-if="selectedOrganizationId">
                <strong>{{ $t("COMMON.ORGANIZATION") }}:</strong>
                <span>{{ organizationName }}</span>
              </div>

              <div class="summary-item" v-if="selectedCustomer">
                <strong>{{ $t("COMMON.CUSTOMER") }}:</strong>
                <span>{{ customerName }}</span>
              </div>

              <div class="summary-item" v-if="paymentData.invoice_code">
                <strong>{{ $t("ELIXIOPAY.INVOICE_CODE") }}:</strong>
                <span>{{ paymentData.invoice_code }}</span>
              </div>

              <div class="summary-item" v-if="paymentData.amount">
                <strong>{{ $t("COMMON.AMOUNT") }}:</strong>
                <span class="text-success font-weight-bold">
                  {{ formatAmount(paymentData.amount, paymentData.currency) }}
                </span>
              </div>

              <div class="summary-item" v-if="paymentData.payment_method">
                <strong>{{ $t("ELIXIOPAY.PAYMENT_METHOD") }}:</strong>
                <span>{{
                  getPaymentMethodLabel(paymentData.payment_method)
                }}</span>
              </div>

              <div class="summary-item" v-if="currentTransaction">
                <strong>{{ $t("ELIXIOPAY.STATUS") }}:</strong>
                <el-tag
                  :type="getStatusType(currentTransaction.status)"
                  size="small"
                >
                  {{ getStatusLabel(currentTransaction.status) }}
                </el-tag>
              </div>
            </div>
          </div>

          <div class="card mt-3" v-if="currentTransaction">
            <div class="card-header">
              <h4 class="mb-0">{{ $t("ELIXIOPAY.TRANSACTION_INFO") }}</h4>
            </div>
            <div class="card-body">
              <div class="summary-item">
                <strong>{{ $t("ELIXIOPAY.TRANSACTION_ID") }}:</strong>
                <span class="text-monospace">{{ currentTransaction.id }}</span>
              </div>

              <div class="summary-item" v-if="currentTransaction.code">
                <strong>{{ $t("ELIXIOPAY.TRANSACTION_CODE") }}:</strong>
                <span class="text-monospace">{{
                  currentTransaction.code
                }}</span>
              </div>

              <div
                class="summary-item"
                v-if="currentTransaction.provider_transaction_id"
              >
                <strong>{{ $t("ELIXIOPAY.ELIXIOPAY_ID") }}:</strong>
                <span class="text-monospace">{{
                  currentTransaction.provider_transaction_id
                }}</span>
              </div>

              <div class="summary-item">
                <strong>{{ $t("COMMON.CREATED_AT") }}:</strong>
                <span>{{ formatDate(currentTransaction.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
<script>
import {
  PROVIDER_PANDAPAY,
  CURRENCY_CAD,
  STATUS_PENDING,
  PAYMENT_METHOD_CB,
} from "@/constants/elixioPayTransactions.js";
import { cloneDeep } from "lodash";
import { Option, Select, Table, TableColumn, Tag } from "element-ui";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import CustomerSelector from "@/components/CustomerSelector.vue";
import "flatpickr/dist/flatpickr.css";

export default {
  layout: "DashboardLayout",
  name: "transaction-form",

  components: {
    BaseInput,
    ValidationError,
    OrganizationSelector,
    [Tag.name]: Tag,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    CustomerSelector,
  },

  mixins: [formMixin],

  props: [
    "transactionData",
    "formErrors",
    "loading",
    "hideOrganization",
    "hideSubmit",
  ],

  data() {
    let transactionData = { ...this.transactionData };
    return {
      selectedOrganizationId:
        transactionData.customer?.organization?.id || null,
      selectedCustomer: null,
      organizationName: "",
      customerName: "",
      paymentData: {
        invoice_code: "",
        amount: null,
        currency: CURRENCY_CAD,
        payment_method: PAYMENT_METHOD_CB,
        description: "",
      },
      paymentUrl: null,
      currentTransaction: null,
      checkingStatus: false,
      CURRENCY_CAD,
      PAYMENT_METHOD_CB,
      transaction: transactionData,
      processing: false,
    };
  },

  computed: {
    canInitiate() {
      return (
        this.selectedOrganizationId &&
        this.selectedCustomer &&
        this.paymentData.invoice_code &&
        this.paymentData.amount > 0 &&
        this.paymentData.currency &&
        this.paymentData.payment_method
      );
    },
  },

  methods: {
    onOrganizationChanged(organizationId) {
      this.selectedOrganizationId = organizationId;
      this.selectedCustomer = null;
    },

    onCustomerChanged(customerId) {
      this.selectedCustomer = customerId;
      this.fetchCustomerName(customerId);
    },

    async fetchOrganizationName(id) {
      try {
        await this.$store.dispatch("organizations/get", id);
        const org = this.$store.getters["organizations/organization"];
        this.organizationName = org.name || "";
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    },

    async fetchCustomerName(id) {
      try {
        await this.$store.dispatch("customers/get", id);
        const customer = this.$store.getters["customers/customer"];
        this.customerName = `${customer.firstname || ""} ${
          customer.lastname || ""
        }`.trim();
      } catch (error) {
        console.error("Error fetching customer:", error);
      }
    },

    async initiatePayment() {
      this.processing = true;

      try {
        const payload = {
          organization_id: this.selectedOrganizationId,
          customer_id: this.selectedCustomer,
          ...this.paymentData,
        };

        const response = await this.$store.dispatch(
          "elixiopayTransactions/initiatePayment",
          payload
        );
        if (response.success) {
          this.paymentUrl = response.data.payment_url;
          this.currentTransaction = {
            id: response.data.transaction_id,
            code: response.data.transaction_code,
            provider_transaction_id: response.data.invoice_id,
            status: "pending",
            created_at: new Date().toISOString(),
          };

          this.$notify({
            type: "success",
            message: this.$t("ELIXIOPAY.PAYMENT_INITIATED"),
          });
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          type: "danger",
          message:
            error.response?.data?.message ||
            this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.processing = false;
      }
    },

    async checkPaymentStatus() {
      if (!this.currentTransaction) return;

      this.checkingStatus = true;

      try {
        const response = await this.$store.dispatch(
          "elixiopayTransactions/checkStatus",
          this.currentTransaction.id
        );

        if (response.success) {
          this.currentTransaction = response.data.transaction;

          this.$notify({
            type: "info",
            message: `${this.$t("ELIXIOPAY.STATUS")}: ${this.getStatusLabel(
              this.currentTransaction.status
            )}`,
          });

          // Si le paiement est complété, rediriger vers la liste
          if (this.currentTransaction.status === "captured") {
            setTimeout(() => {
              this.$router.push({ name: "List PandaPayTransactions" });
            }, 2000);
          }
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.checkingStatus = false;
      }
    },

    resetForm() {
      this.paymentUrl = null;
      this.currentTransaction = null;
      this.paymentData = {
        invoice_code: "",
        amount: null,
        currency: this.CURRENCY_CAD,
        payment_method: this.PAYMENT_METHOD_CB,
        description: "",
      };
    },

    onIframeLoad() {
      console.log("Payment iframe loaded");
    },

    getStatusType(status) {
      const types = {
        pending: "warning",
        authorized: "info",
        captured: "success",
        failed: "danger",
        refunded: "warning",
        cancelled: "info",
      };
      return types[status] || "info";
    },

    getStatusLabel(status) {
      const labels = {
        pending: this.$t("ELIXIOPAY.STATUS_PENDING"),
        authorized: this.$t("ELIXIOPAY.STATUS_AUTHORIZED"),
        captured: this.$t("ELIXIOPAY.STATUS_CAPTURED"),
        failed: this.$t("ELIXIOPAY.STATUS_FAILED"),
        refunded: this.$t("ELIXIOPAY.STATUS_REFUNDED"),
        cancelled: this.$t("ELIXIOPAY.STATUS_CANCELLED"),
      };
      return labels[status] || status;
    },

    getPaymentMethodLabel(method) {
      const labels = {
        CB: this.$t("ELIXIOPAY.METHOD_CB"),
        TFE: this.$t("ELIXIOPAY.METHOD_TFE"),
      };
      return labels[method] || method;
    },

    formatAmount(amount, currency) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: currency || "CAD",
      }).format(amount);
    },

    formatDate(date) {
      return new Date(date).toLocaleString("fr-FR");
    },
    onFormChanged() {
      this.$emit("formChanged");
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
    transactionData(transactionData) {
      if (transactionData) {
        this.transaction = {
          ...this.transaction,
          ...cloneDeep(transactionData),
        };
      }
    },
  },
};
</script>

<style scoped>
.payment-iframe-container {
  position: relative;
  width: 100%;
  padding-bottom: 75%; /* Ratio 4:3 */
  background: #f8f9fc;
  border: 2px solid #e3e6f0;
  border-radius: 8px;
  overflow: hidden;
}

.payment-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item strong {
  color: #5a5c69;
}

.summary-item span {
  color: #3a3b45;
  text-align: right;
}
</style>
